<template>
  <div>
    <loading-container v-if="loading"></loading-container>

    <div v-else class="container">
      <div class="row">
        <div class="col-xs-6">
          <help-text>
            <h3>
              Details
            </h3>

            <p>
              Edit the details of the person, including the name and basic contact info.
            </p>
          </help-text>
        </div>

        <div class="col-xs-18">
          <form @submit.prevent="submit">
            <div class="panel panel-default">
              <div class="panel-body form-horizontal">
                <StaticFormGroup
                    :value="personData.legacy_id"
                    label="Legacy ID"
                    help-text="The ID of the person in the legacy system."
                />

                <TextFormGroup
                    v-model="personData.name"
                    name="name"
                    label="Name"
                    help-text="The name of the person."
                    :error="getError('name')"
                />

                <TextFormGroup
                    v-model="personData.title"
                    name="title"
                    label="Title"
                    help-text="The title of the person."
                    :error="getError('title')"
                />

                <EmailFormGroup
                    v-model="personData.email"
                    name="email"
                    label="Email"
                    help-text="The person's email."
                    :error="getError('email')"
                />

                <FormGroup
                    name="alternate_emails"
                    label="Alternate Emails"
                    help-text="Any alternate email addresses that the person uses."
                    :error="getError('alternate_emails')"
                >
                  <VueTagsInput
                      v-model="editingAlternateEmails"
                      placeholder="Add email"
                      :tags="alternateEmails"
                      @tags-changed="$emit('input', alternateEmails = $event)"
                  />
                </FormGroup>

                <TextFormGroup
                    v-model="personData.display_title"
                    name="display_title"
                    label="Display Title"
                    help-text="The display title of the person."
                    :error="getError('display_title')"
                />

                <TextFormGroup
                    v-model="personData.cell_phone"
                    name="cell_phone"
                    label="Cell Phone"
                    help-text="The person's cell phone number."
                    :error="getError('cell_phone')"
                />

                <TextFormGroup
                    v-model="personData.work_phone"
                    name="work_phone"
                    label="Work Phone"
                    help-text="The person's work phone number."
                    :error="getError('work_phone')"
                />

                <TagsFormGroup
                    v-model="tags"
                    name="tags"
                    label="Tags"
                    help-text="Any number of tags that describe the company."
                    :error="getError('tags')"
                />

                <SelectFormGroup
                    v-if="hasPermission('admin')"
                    v-model="personData.payment_condition_id"
                    name="payment_condition_id"
                    label="Payment Condition"
                    help-text="The payment condition that will be used for the person's invoices."
                    :error="getError('payment_condition_id')"
                    :options="paymentConditionOptions"
                    :placeholder="'Default (' + defaultPaymentCondition.attributes.name + ')'"
                    :nullable="true"
                />

                <RadioFormGroup
                    v-model="personData.is_active"
                    name="is_active"
                    label="Active"
                    help-text="A flag indicating whether the person is active."
                    :error="getError('is_active')"
                    :options="[{value: true, label: 'Yes'}, {value: false, label: 'No'}]"
                />

                <RadioFormGroup
                    v-model="personData.has_trigger_mails"
                    name="has_trigger_mails"
                    label="Has Trigger Mails"
                    help-text="A flag indicating if trigger mails are enabled."
                    :error="getError('has_trigger_mails')"
                    :options="[{value: true, label: 'Yes'}, {value: false, label: 'No'}]"
                />

                <TypeaheadFormGroup
                    v-model="personData.new_person_id"
                    name="new_person_id"
                    label="New Person"
                    help-text="The person that was created as a replacement for this person."
                    :error="getError('new_person_id')"
                    endpoint="/api/v2/people"
                    :token="shared.token"
                    path="data.data"
                    placeholder="Search..."
                    :initial-label="personData.new_person ? personData.new_person.name : ''"
                    :result-label-property="row => row.attributes.name"
                />

                <GalleryFormGroup
                    v-model="personData.image_id"
                    name="image_id"
                    label="Image"
                    help-text="The image of the person."
                    :error="(errors.image_id || [])[0]"
                    :nullable="true"
                />

                <TypeaheadFormGroup
                    v-model="personData.company_id"
                    name="company_id"
                    label="Company"
                    help-text="The company that the user belongs to."
                    :error="getError('company_id')"
                    endpoint="/api/v2/companies"
                    :token="shared.token"
                    path="data.data"
                    placeholder="Search..."
                    :initial-label="personData.company ? personData.company.name : ''"
                    :result-label-property="row => row.attributes.name"
                />

                <LabelFormGroup
                    label="Has Partner Access"
                    :value="personData.has_partner_access ? 'Yes' : 'No'"
                    :type="personData.has_partner_access ? 'success' : 'warning'"
                />
              </div>
            </div>

            <AddressPanel
                v-model="personData.address"
                :toggleable="true"
                :countries="countries"
                :errors="errors"
            />

            <hr class="panel-separator">

            <button type="submit" class="btn btn-black" style="margin-bottom: 22px;">
              <i class="fa fa-save"></i> Save
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from '@/http'
import notify from '@/notify'
import store from '@/store'
import AddressPanel from '@/components/AddressPanel'
import EmailFormGroup from '@/vendor/forms/FormGroups/Horizontal/Email'
import Errors from '@/mixins/Errors'
import FormGroup from '@/vendor/forms/FormGroups/Horizontal/FormGroup'
import HelpText from '@/components/HelpText'
import Load from '@/mixins/Load'
import LoadingContainer from '@/components/Layout/LoadingContainer'
import Permissions from '@/mixins/Permissions'
import RadioFormGroup from '@/vendor/forms/FormGroups/Horizontal/Radio'
import SelectFormGroup from '@/vendor/forms/FormGroups/Horizontal/Select'
import StaticFormGroup from '@/vendor/forms/FormGroups/Horizontal/Static'
import TagsFormGroup from '@/components/FormGroups/Horizontal/Tags'
import TextFormGroup from '@/vendor/forms/FormGroups/Horizontal/Text'
import TypeaheadFormGroup from '@/vendor/forms/FormGroups/Horizontal/Typeahead'
import VueTagsInput from '@johmun/vue-tags-input'
import GalleryFormGroup from "../../../../../components/FormGroups/Horizontal/Gallery"
import LabelFormGroup from "../../../../../vendor/forms/FormGroups/Horizontal/Label.vue";

export default {
  components: {
    AddressPanel,
    EmailFormGroup,
    FormGroup,
    HelpText,
    LoadingContainer,
    RadioFormGroup,
    SelectFormGroup,
    StaticFormGroup,
    TagsFormGroup,
    TextFormGroup,
    TypeaheadFormGroup,
    VueTagsInput,
    GalleryFormGroup,
    LabelFormGroup,
  },

  mixins: [Errors, Load, Permissions],

  model: {
    prop: 'person',
    event: 'person-updated'
  },

  props: ['person'],

  data() {
    return {
      paymentConditions: [],
      countries: [],
      personData: {...this.person.data},
      tags: this.person.data.tags.map(tag => {
        return {text: tag.name, style: 'background-color:' + tag.color + ';color:#ffffff'}
      }),
      shared: store,
      editingAlternateEmails: '',
      alternateEmails: this.person.data.alternate_emails.map(email => {
        return {text: email}
      })

    }
  },

  mounted() {
    this.load({
      paymentConditions: '/api/v2/payment-conditions',
    })
  },

  computed: {
    paymentConditionOptions() {
      return this.paymentConditions.data.map(paymentCondition => {
        return {
          value: paymentCondition.id,
          label: paymentCondition.attributes.name
        }
      })
    },

    defaultPaymentCondition() {
      return this.paymentConditions.data.find(paymentCondition => paymentCondition.attributes.is_default)
    },

    requestData() {
      return {
        ...this.personData,
        tags: this.tags.map(tag => tag.text),
        alternate_emails: this.alternateEmails.map(alternateEmails => alternateEmails.text)
      }
    }
  },

  methods: {
    submit() {
      http.put('/api/v1/people/' + this.person.data.id, this.requestData)
          .then(response => {
            notify.success('The person was saved')

            this.$emit('person-updated', response.data)
          })
          .catch(error => {
            if (error.response.status === 400) {
              return notify.error(error.response.data.message)
            }

            throw error
          })
          .catch(this.catchValidationErrors)
    }
  }
}
</script>
